import { useState, useEffect, useCallback, useMemo } from "react";
import { listTenantWorkspaceDirectoryUsers } from "@/api/tenantWorkspaceDirectoryApi";
import { normalizeWorkspaceEmail } from "@/api/workspacesApi";

/**
 * @param {{ open: boolean, onClose: () => void, onCreate: (p: { name: string, businessUnit?: string, memberEmails: string[], inviteEmails: string[] }) => Promise<void> }} props
 */
export default function CreateWorkspaceModal({ open, onClose, onCreate }) {
  const [name, setName] = useState("");
  const [businessUnit, setBusinessUnit] = useState("");
  const [directory, setDirectory] = useState([]);
  const [selected, setSelected] = useState(/** @type {Set<string>} */ (new Set()));
  const [inviteEmails, setInviteEmails] = useState(/** @type {string[]} */ ([]));
  const [inviteDraft, setInviteDraft] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [loadBusy, setLoadBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;
    setName("");
    setBusinessUnit("");
    setUserSearch("");
    setFormError("");
    setBusy(false);
    setSelected(new Set());
    setInviteEmails([]);
    setInviteDraft("");
    let cancelled = false;
    (async () => {
      setLoadBusy(true);
      try {
        const { data } = await listTenantWorkspaceDirectoryUsers();
        if (!cancelled) setDirectory(data.users);
      } catch (err) {
        if (!cancelled) {
          setDirectory([]);
          setFormError(
            err instanceof Error
              ? err.message
              : "Could not load tenant user list from the server. If you still see sample names, hard-refresh the app or restart `npm run dev` so the UI is not using an old bundle."
          );
        }
      } finally {
        if (!cancelled) setLoadBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return directory;
    return directory.filter(
      (u) => u.name.toLowerCase().includes(q) || String(u.email).toLowerCase().includes(q)
    );
  }, [directory, userSearch]);

  const toggleUser = useCallback((email) => {
    const key = normalizeWorkspaceEmail(email);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const addInviteEmail = useCallback(() => {
    const key = normalizeWorkspaceEmail(inviteDraft);
    if (!key || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(key)) {
      setFormError("Enter a valid email address to invite.");
      return;
    }
    if (selected.has(key) || inviteEmails.includes(key)) {
      setFormError("That email is already selected.");
      return;
    }
    setInviteEmails((prev) => [...prev, key]);
    setInviteDraft("");
    setFormError("");
  }, [inviteDraft, inviteEmails, selected]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setFormError("");
      const trimmed = name.trim();
      if (!trimmed) {
        setFormError("Enter a workspace name.");
        return;
      }
      setBusy(true);
      try {
        await onCreate({
          name: trimmed,
          businessUnit: businessUnit.trim() || undefined,
          memberEmails: [...selected],
          inviteEmails,
        });
        onClose();
      } catch (err) {
        setFormError(err instanceof Error ? err.message : "Could not create workspace.");
      } finally {
        setBusy(false);
      }
    },
    [name, businessUnit, selected, inviteEmails, onCreate, onClose]
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="ui-card-static flex max-h-[min(92vh,720px)] w-full max-w-lg flex-col overflow-hidden !p-0 sm:!p-0"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-ws-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 border-b border-slate-100 px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id="create-ws-title" className="text-lg font-semibold tracking-tight text-slate-900">
                New workspace
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Separate operations per business unit. Your account is always added; pick teammates below.
              </p>
            </div>
            <button type="button" onClick={onClose} className="ui-btn-icon shrink-0" aria-label="Close">
              <i className="ri-close-line text-xl" aria-hidden />
            </button>
          </div>
        </div>

        <form className="flex min-h-0 flex-1 flex-col overflow-hidden" onSubmit={(e) => void handleSubmit(e)}>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4 scrollbar-thin">
            {formError ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
                {formError}
              </div>
            ) : null}
            <div>
              <label htmlFor="ws-new-name" className="mb-1.5 block text-sm font-semibold text-slate-800">
                Workspace name
              </label>
              <input
                id="ws-new-name"
                className="ui-field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. EMEA Support"
                autoComplete="off"
                disabled={busy}
              />
            </div>
            <div>
              <label htmlFor="ws-new-bu" className="mb-1.5 block text-sm font-semibold text-slate-800">
                Business unit <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <input
                id="ws-new-bu"
                className="ui-field"
                value={businessUnit}
                onChange={(e) => setBusinessUnit(e.target.value)}
                placeholder="e.g. B2B · Retail"
                autoComplete="organization"
                disabled={busy}
              />
            </div>

            <div>
              <p className="mb-2 text-sm font-semibold text-slate-800">Assign users</p>
              <p className="mb-2 text-xs text-slate-500">Anyone selected receives access to this workspace (in addition to you).</p>
              <div className="relative mb-2">
                <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 text-slate-400">
                  <i className="ri-search-line text-sm" aria-hidden />
                </span>
                <input
                  type="search"
                  className="ui-input !pl-9"
                  placeholder="Filter directory…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  disabled={busy || loadBusy}
                  aria-label="Filter users"
                />
              </div>
              <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-100 bg-slate-50/50 scrollbar-thin">
                {loadBusy ? (
                  <p className="px-3 py-6 text-center text-sm text-slate-500">Loading users…</p>
                ) : filteredUsers.length === 0 ? (
                  <p className="px-3 py-6 text-center text-sm text-slate-500">No users in directory.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {filteredUsers.map((u) => {
                      const key = normalizeWorkspaceEmail(u.email);
                      const checked = selected.has(key);
                      return (
                        <li key={u.id}>
                          <label className="flex cursor-pointer items-center gap-3 px-3 py-2 transition-colors hover:bg-white">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                              checked={checked}
                              onChange={() => toggleUser(u.email)}
                              disabled={busy}
                            />
                            <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{u.name}</span>
                            <span className="hidden max-w-[40%] truncate text-xs text-slate-500 sm:inline">{u.email}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <p className="mt-2 text-[11px] text-slate-400">{selected.size} user{selected.size === 1 ? "" : "s"} selected (besides you).</p>
            </div>

            <div>
              <p className="mb-2 text-sm font-semibold text-slate-800">Invite new emails</p>
              <p className="mb-2 text-xs text-slate-500">
                Addresses that are not in the directory receive an invitation and gain access after they accept.
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  className="ui-field flex-1"
                  placeholder="new.agent@example.com"
                  value={inviteDraft}
                  onChange={(e) => setInviteDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addInviteEmail();
                    }
                  }}
                  disabled={busy}
                />
                <button type="button" className="ui-btn-secondary" onClick={addInviteEmail} disabled={busy}>
                  Add
                </button>
              </div>
              {inviteEmails.length ? (
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {inviteEmails.map((email) => (
                    <li
                      key={email}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700"
                    >
                      {email}
                      <button
                        type="button"
                        className="text-slate-400 hover:text-slate-700"
                        aria-label={`Remove ${email}`}
                        onClick={() => setInviteEmails((prev) => prev.filter((item) => item !== email))}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-100 px-6 py-4">
            <button type="button" className="ui-btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="ui-btn-primary" disabled={busy}>
              {busy ? "Creating…" : "Create workspace"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
